import {
  docNumberHandler,
  initialAddress,
  initialEstimate,
  initialInvoice,
  initialProductLine,
} from 'data/documents/initialData'
import {
  DOCUMENTTYPE,
  Invoice,
  PaidState,
  ProductLine,
  TPayment,
} from 'data/documents/types'
import dayjs from 'dayjs'
import { action, makeObservable, observable, runInAction, when } from 'mobx'
import { createContext } from 'react'
import { toast } from 'react-toastify'
import fileService from 'services/file-service/file.service'
import invoicesService from 'services/invoices-service/invoices.service'
import { DiscountType, DueType, GeneratorInvoice, IFilterModel, SentState } from 'services/invoices-service/types'
import helpers from 'utils/helpers'
import { v4 as uuidv4 } from 'uuid'
import authStore from './AuthStore'
import LocalStorageManager from 'manager/local-storage.manager'
import { MAX_FREE_DOCUMENTS } from 'data/global/constants'

const defaultInvoice: GeneratorInvoice = {
  number: '436',
  documentType: 'Invoice',
  issueDate: 1720781695245,
  dueDate: 1720781695245,
  dueType: 'CUSTOM_DATE',
  documentClient: {
    name: 'Willie Mitchell',
    email: 'williemitchell@mail.com',
    billingAddress: {
      addressLine1: '2633 Fred Jackson Way',
      addressLine2: 'Richmond',
      postalCode: '94801',
      country: 'CA',
      city: '',
      state: '',
    },
    mobilePhone: null,
    phone: '800-564-5985',
    clientId: 'bb768385-8af1-4e82-8624-a5cf6990ce98',
    note: null,
    shippingAddress: {
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
      country: '',
      city: '',
      state: '',
    },
  },
  documentCompany: {
    businessName: 'A Company',
    email: 'sale@company.com',
    taxNumber: null,
    terms: null,
    mobilePhone: null,
    phone: '800-558-9558',
    website: 'company.com',
    logo: 'data:image/png;base64,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',
    signature: null,
    ownerName: null,
    address: {
      addressLine1: 'Maple Ave',
      addressLine2: 'North Town',
      postalCode: '88888',
      country: 'CA',
      city: '',
      state: '',
    },
    companyId: '79541258-7e93-4df6-934b-70e78b0a1d9e',
  },
  total: 1546000,
  subtotal: 1940000,
  documentItems: [
    {
      itemName: 'Prototype',
      itemDescription:
        'Prototype-based programming is a style of object-oriented programming in which behaviour',
      qty: 64,
      total: 100,
      subtotal: 100,
      price: 100,
      order: 0,
      discount: null,
      tax: {
        name: null,
        rate: 1,
      },
      isTaxable: true,
      taxAmount: 1,
    },
    {
      itemName: 'Design',
      price: 75,
      qty: 120,
      discount: 10,
      total: 9000,
      subtotal: 100,
      order: 2,
      tax: {
        name: null,
        rate: 10,
      },
      isTaxable: true,
      taxAmount: 10,
    },
    {
      itemName: 'Programming',
      price: 75,
      qty: 9,
      total: 8000,
      subtotal: 100,
      discount: null,
      order: 3,
      tax: {
        name: null,
        rate: 0,
      },
      isTaxable: true,
      taxAmount: 0,
    },
  ],
  taxes: [
    {
      name: 'Taxes',
      rate: 10,
    },
  ],
  shippingAmount: 100,
  discount: {
    type: 'AMOUNT',
    amount: 100,
    percentage: 0,
  },
  currency: {
    code: 'USD',
    symbol: '$',
  },
  paymentInstructions:
    'Paypal email:\nmoney@company.com\nBank transfer:\nRouting (ABA): 063320086',
  taxType: 'PER_ITEM',
  taxIncluded: false,
}

export class InvoicesStore {
  invoices: any[]
  estimates: any[]
  documents: any[]
  shareLink: string
  sendToEmailData: {
    isOpen: boolean
    emailToSend: string | undefined
    sendState: string | undefined
    invoiceId?: string
    estimateId?: string
  }
  currentGeneratorDoc: Invoice | any | undefined
  currentGeneratorId: string | undefined
  invoicesFilterModel: IFilterModel
  isPayWall: boolean
  isPayWallSource: string | undefined
  docAmount: number
  estimatesFilterModel: IFilterModel
  totalInvoices: number
  totalEstimates: number
  total: number
  isLoading: boolean
  debounceTimer: number | null | undefined
  paywallTimeoutTimerId: NodeJS.Timeout | null
  generatorInvoice: GeneratorInvoice | null

  constructor() {
    makeObservable(this, {
      invoices: observable,
      estimates: observable,
      documents: observable,
      invoicesFilterModel: observable,
      isPayWall: observable,
      isPayWallSource: observable,
      isLoading: observable,
      docAmount: observable,
      estimatesFilterModel: observable,
      total: observable,
      totalInvoices: observable,
      totalEstimates: observable,
      currentGeneratorDoc: observable,
      currentGeneratorId: observable,
      updatePaymentInstructions: observable,
      shareLink: observable,
      debounceTimer: observable,
      sendToEmailData: observable,
      getEstimates: action,
      sendToEmailDataToOpen: action,
      sendToEmailDataClear: action,
      openPreviewDocument: action,
      shareLinkClear: action,
      getInvoices: action,
      getDocuments: action,
      updateEstimatesFilter: action,
      updateInvoicesFilter: action,
      resetInvoices: action,
      mapDocumentForGenerator: action,
      uploadPhoto: action,
      updateCurrentGenDoc: action,
      setIsPaywall: action,
      shareLinkHandler: action,
      getDocAmount: action,
      postInvoice: action,
      postBaseInvoice: action,
      getPaid: action,
      demapDocument: action,
      getDueDateByType: action,
      getIsOverDue: action,
      setGenDoc: action,
      getPaidState: action,
      setIsPaywallSource: action,
      setGeneratorInvoice: action,
      generatorInvoice: observable
    })

    this.invoices = []
    this.estimates = []
    this.documents = []
    this.shareLink = ''
    this.invoicesFilterModel = {
      currency: helpers?.getCurrency().code,
      dateRange: undefined,
      dateFrom: undefined,
      dateTo: undefined,

      sortBy: 'issueDate',
      search: undefined,
      offset: 0,
      limit: 10,
      sortType: 'DESC',
    }
    this.estimatesFilterModel = {
      currency: helpers?.getCurrency().code,
      dateRange: undefined,
      dateFrom: undefined,
      dateTo: undefined,

      sortBy: 'issueDate',
      search: undefined,
      offset: 0,
      limit: 10,
      sortType: 'DESC',
    }
    this.sendToEmailData = {
      isOpen: false,
      emailToSend: undefined,
      sendState: undefined,
      invoiceId: undefined,
      estimateId: undefined,
    }
    this.currentGeneratorId = undefined
    this.totalEstimates = 0
    this.total = 0
    this.totalInvoices = 0
    this.debounceTimer = null
    this.isPayWall = false
    this.isPayWallSource = undefined
    this.isLoading = false
    this.docAmount = 0
    this.paywallTimeoutTimerId = null
    this.generatorInvoice = LocalStorageManager.get<GeneratorInvoice>("jsonInvoice") || null
    when(
      () => this.isPayWall,
      () => {
        localStorage.setItem(`paywall-timeout-${authStore.user?.uid}`, 'true')
        if (this.paywallTimeoutTimerId) {
          clearTimeout(this.paywallTimeoutTimerId)
        }
      }
    )
  }

  get isPaywallShowed() {
    return (
      localStorage.getItem(`paywall-timeout-${authStore.user?.uid}`) === 'true'
    )
  }

  async getDocuments() {
    this.isLoading = true

    try {
      await this.getInvoices()
      await this.getEstimates()

      runInAction(() => {
        this.documents = [...this.invoices, ...this.estimates]
        this.total = (this.totalInvoices || 0) + (this.totalEstimates || 0)
        if (
          !this.documents.length &&
          !this.isPaywallShowed &&
          !authStore.subscriptionIsActive &&
          !this.paywallTimeoutTimerId
        ) {
          this.paywallTimeoutTimerId = setTimeout(() => {
            runInAction(() => {
              this.setIsPaywall(true)
            })
          }, 3 * 60 * 1000)
        }
      })
    } catch (err) {
      // getInvoicesError()
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async updateInvoicesFilter(filterModel: IFilterModel) {
    this.invoicesFilterModel = { ...this.invoicesFilterModel, ...filterModel }
    await this.getInvoices()

    runInAction(() => {
      this.documents = [...this.invoices, ...this.estimates]
      this.total = (this.totalInvoices || 0) + (this.totalEstimates || 0)
    })
  }

  async shareLinkHandler(invoiceId: string, estimateId: string) {
    this.isLoading = true

    try {
      toast.info('Generating link, wait please.')

      const link = await invoicesService.shareLink({
        invoiceId: invoiceId || undefined,
        estimateId: estimateId || undefined,
      })

      if (link) {
        runInAction(() => {
          this.shareLink = link
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  setGeneratorInvoice(data: GeneratorInvoice) {
    this.generatorInvoice = data
    LocalStorageManager.set('jsonInvoice', data);
  }

  clearGeneratorInvoice() {
    this.generatorInvoice = null
    LocalStorageManager.delete('jsonInvoice');
  }

  async openPreviewDocument(invoiceId: string, estimateId: string) {
    this.isLoading = true

    try {
      toast.info('Generating link, wait please.')
      const link = await invoicesService.shareLink({
        invoiceId: invoiceId || undefined,
        estimateId: estimateId || undefined,
      })

      if (link) {
        window.open(link, '_blank')
      }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async shareLinkClear() {
    this.shareLink = ''
  }

  async sendToEmailDataToOpen(
    emailToSend?: string,
    invoiceId?: string,
    estimateId?: string,
    sendState?: string
  ) {
    this.sendToEmailData = {
      isOpen: true,
      emailToSend,
      invoiceId,
      estimateId,
      sendState,
    }
  }

  async sendToEmailDataClear() {
    this.sendToEmailData = {
      isOpen: false,
      emailToSend: undefined,
      sendState: undefined,
      invoiceId: undefined,
      estimateId: undefined,
    }
  }

  async updateEstimatesFilter(filterModel?: IFilterModel) {
    this.estimatesFilterModel = {
      ...this.estimatesFilterModel,
      ...filterModel,
    }
    await this.getEstimates()
    runInAction(() => {
      this.documents = [...this.invoices, ...this.estimates]
      this.total = (this.totalInvoices || 0) + (this.totalEstimates || 0)
    })
  }

  async getInvoices() {
    try {
      const data = await invoicesService.getInvoices(this.invoicesFilterModel)

      runInAction(() => {
        this.invoices =
          data.results?.map(
            (el: any) => {
              const paid = this.getPaid(el.payments)
              const paidState = this.getPaidState(el.total || 0, paid || 0)
              const dueDateByType = this.getDueDateByType(el.dueType, el)
              const isOverDue = this.getIsOverDue(paidState, dueDateByType)

              return {
                ...el,
                __dueDateByType: dueDateByType,
                __isOverDue: isOverDue,
                __isDraft: el.sentState === SentState.DRAFT,
                __balanceDue: el.total - paid,
                __paidState: paidState,
                __type: DOCUMENTTYPE.INV,
              }
            }
          ) || []
        this.totalInvoices = data.total || 0
      })
    } catch (err) {
      console.error(err)
    }
  }

  async getEstimates() {
    try {
      const data = await invoicesService.getEstimates(this.estimatesFilterModel)

      runInAction(() => {
        this.estimates = data.results.map(
          (el: any) => {
            const paid = this.getPaid(el.payments)
            const paidState = this.getPaidState(el.total || 0, paid || 0)

            return {
              ...el,
              __isDraft: el.sentState === SentState.DRAFT,
              __balanceDue: el.total - paid,
              __paidState: paidState,
              __type: DOCUMENTTYPE.EST,
            }
          }
        )
        this.totalEstimates = data.total || 0
      })
    } catch (err) {
      console.error(err)
    }
  }

  getPaid(payments: any[]) {
    if (!payments) return ''
    return Array.from(payments).reduce((a: any, b: { amount: number }) => {
      return a + +b.amount
    }, 0)
  }

  async uploadPhoto(photo: string): Promise<string | undefined> {
    try {
      if (photo?.includes('base64')) {
        const { formData, newFileName } = helpers.getFormDataFromBase64(photo)
        const logoUUID = await fileService.postFile(formData)

        if (logoUUID) {
          toast.info(`photo uploaded`)
        }

        return logoUUID ? newFileName : undefined
      }
    } catch (error) {
      console.error(error)
    }
  }

  async updateCurrentGenDoc(inv: Invoice | null, noPost?: boolean) {
    const documentNumber = !inv?.documentNumber
      ? docNumberHandler()
      : inv.documentNumber
    this.currentGeneratorDoc = { ...inv, documentNumber }
    if (!noPost && inv) {
      await this.postInvoice({ ...inv, documentNumber })
    }
  }

  async sendDocument(
    mail: string,
    invoiceId: string,
    estimateId: string,
    onValid?: () => void,
    onSuccess?: () => void
  ) {
    if (helpers.isValidEmail(mail)) {
      if (onValid) onValid()

      await invoicesService.shareEmail({
        invoiceId: invoiceId || undefined,
        estimateId: estimateId || undefined,
        recipient: mail,
      })

      onSuccess?.()
    } else {
      toast.error('not valid client email to send.')
    }
  }

  async updatePaymentInstructions(paymentInstructions: any) {
    if (this.currentGeneratorDoc) {
      this.currentGeneratorDoc = {
        ...this.currentGeneratorDoc,
        paymentInstructions,
      }

      await this.postInvoice({
        ...this.currentGeneratorDoc,
        paymentInstructions,
      })
    }
  }

  async setGenDoc(
    id: string,
    type: DOCUMENTTYPE,
    paymentInstructions: string,
    history: any,
    isActiveSubscription: boolean
  ) {
    const initialState =
      type === DOCUMENTTYPE.EST
        ? {
            ...initialEstimate(id),
          }
        : {
            ...initialInvoice(id),
          }

    let data
    if (typeof id === 'string' && id?.length > 10) {
      data = await invoicesService[
        type === DOCUMENTTYPE.INV ? 'getInvoice' : 'getEstimate'
      ](id)
    }

    if (data?.archived) {
      toast.error('This document has been deleted')
      history.push('/all-documents/INV')

      return
    }

    if (!isActiveSubscription && !data) {
      try {
        if (this.total >= MAX_FREE_DOCUMENTS) {
          this.setIsPaywall(true)

          return
        }
        const docAmount = await invoicesService.getAmount()

        if (docAmount >= MAX_FREE_DOCUMENTS) {
          this.setIsPaywall(true)

          return
        }
      } catch (error) {
        console.error(error)
      }
    }

    const currentState = data
      ? await this.mapDocumentForGenerator(data, paymentInstructions, type)
      : { ...initialState, documentNumber: docNumberHandler() }

    this.currentGeneratorDoc = {
      ...currentState,
      __type: type === DOCUMENTTYPE.EST ? DOCUMENTTYPE.EST : DOCUMENTTYPE.INV,
      paymentInstructions:
        currentState?.paymentInstructions || paymentInstructions,
    }
  }

  // for base document entity
  async postBaseInvoice(inv: any) {
    try {
      let doc: any = {}
      const invoice = { ...this.demapDocument(inv), ...inv }
      const updatedAt = new Date().getTime()

      if (inv.__type === DOCUMENTTYPE.EST) {
        doc = await invoicesService.postEstimate({
          ...invoice,
          updatedAt,
          discount: invoice.discount?.type ? invoice.discount : null,
          documentItems: invoice.documentItems?.map(
            (el: { discount: { type: any } }) => {
              return {
                ...el,
                discount: el.discount?.type ? el.discount : null,
              }
            }
          ),
        })

        doc.__type = DOCUMENTTYPE.EST
      } else if (inv.__type === DOCUMENTTYPE.INV) {
        doc = await invoicesService.postInvoice({
          ...invoice,
          updatedAt,
          discount: invoice.discount?.type ? invoice.discount : null,
          documentItems: invoice.documentItems?.map(
            (el: { discount: { type: any } }) => {
              return {
                ...el,
                discount: el.discount?.type ? el.discount : null,
              }
            }
          ),
        })
        doc.__type = DOCUMENTTYPE.INV
      }

      const paid = this.getPaid(doc.payments)
      const paidState = this.getPaidState(doc.total || 0, paid || 0)
      const dueDateByType = this.getDueDateByType(doc.dueType, doc)
      const isOverDue = this.getIsOverDue(paidState, dueDateByType)

      doc = {
        ...doc,
        documentItems: doc.documentItems?.map(
          (el: { discount: { type: any } }) => {
            return { ...el, discount: el.discount?.type ? el.discount : null }
          }
        ),
        __dueDateByType: dueDateByType,
        __isOverDue: isOverDue,
        __isDraft: doc.sentState === SentState.DRAFT,
        __balanceDue: doc.total - paid,
        __paidState: paidState,
      }

      return doc
    } catch (error) {
      console.error(error)
    }
  }

  // for generator's entities
  async postInvoice(inv: Invoice) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this
    return new Promise((res, rej) => {
      if (_this.debounceTimer) {
        clearTimeout(_this.debounceTimer)
      }
      runInAction(() => {
        _this.isLoading = true
      })
      // @ts-ignore
      _this.debounceTimer = setTimeout(async () => {
        const invoice = _this.demapDocument(inv)
        try {
          if (inv.__type === DOCUMENTTYPE.EST) {
            await invoicesService.postEstimate(invoice)
          } else if (inv.__type === DOCUMENTTYPE.INV) {
            await invoicesService.postInvoice(invoice)
          }
        } catch (error) {
          console.error(error)
        } finally {
          runInAction(() => {
            this.isLoading = false
          })
          res()
        }

        if (_this.debounceTimer) {
          clearTimeout(_this.debounceTimer)
          _this.debounceTimer = undefined
        }
      }, 2000)
    })
  }

  demapDocument(d: Invoice | any) {
    return {
      id: d.id || uuidv4(),
      number: d.documentNumber || '',
      updatedAt: new Date().getTime(),
      createdAt: d.createdAt || new Date().getTime(),
      sentState: d.sentState || SentState.DRAFT,
      documentType: d.documentType,
      issueDate: d.issueDate
        ? new Date(d.issueDate).getTime()
        : new Date().getTime(),
      dueDate: d.dueDate ? new Date(d.dueDate).getTime() : new Date().getTime(),
      dueType: this.getDueTypeByDate(d),
      documentClient: !!d.clientId
        ? {
            name: d.clientName,
            email: d.clientEmail || null,
            billingAddress: {
              addressLine1: d.clientBillingAddress?.addressLine1 || null,
              addressLine2: d.clientBillingAddress?.addressLine2 || null,
              city: d.clientBillingAddress?.city || null,
              country: d.clientBillingAddress?.country || null,
              zip: d.clientBillingAddress?.zip || null,
              state: d.clientBillingAddress?.state || null,
            },
            mobilePhone: d.clientMobile || null,
            phone: d.clientPhone || null,
            clientId: d.clientId || null,
            note: d.clientNote || null,
            taxNumber: d.taxNumber || null,
            shippingAddress: d.clientShippingAddress || initialAddress,
          }
        : null,
        documentLegalEntity: !!d.companyId
        ? {
            legalName: d.companyName,
            email: d.companyEmail || null,
            taxNumber: d.companyTaxNumber || null,
            noteForClients: d.companyNotes || null,
            phone: d.companyPhone || null,
            website: d.companyWebsite || null,
            logo: d.logo || null,
            signature: d.signature || null,
            address: d.companyAddress || initialAddress,
            legalEntityId: d.companyId || null,
            disablePaymentLinks: d.disablePaymentLinks ?? true
          }
        : null,
      total: +d.total || 0,
      subtotal: +d.subTotal || 0,
      isPartialPaymentsAllowed: true,
      photos: d.photos,
      note: d.notes || null,
      terms: d.terms || null,
      signature: d.signature || null,
      uiFormatter: d.uiFormatter || null,
      documentItems: d.documentItems
        ?.filter((el: ProductLine) => !!el.itemName)
        .sort((a: ProductLine, b: ProductLine) =>
          typeof +a.itemOrder === 'number' && typeof +b.itemOrder === 'number'
            ? +a.itemOrder - +b.itemOrder
            : 0
        )
        .map((i: ProductLine, index: number) => {
          return {
            itemName: i.itemName || '',
            qty: +i.qty || null,
            total: +i.itemTotal || 0,
            subtotal: +i.itemSubTotal || 0,
            price: +i.itemPrice || null,
            order: index,
            discount: !!+i.itemDiscountAmount
              ? {
                  type: i.itemDiscountType || null,
                  amount: +i.itemDiscountAmount || null,
                  percentage: +i.itemDiscountPercentage || null,
                }
              : null,
            tax: i.itemTax
              ? {
                  name: i.itemTaxName || null,
                  rate: +i.itemTax || null,
                }
              : null,
            isTaxable: i.isTaxable || false,
            taxAmount: +i.itemTaxAmount || 0,
          }
        }),
      payments: d.payments?.map((p: TPayment) => {
        return {
          id: p.id || uuidv4(),
          amount: +p.amount || null,
          type: p.type || 'Other',
          updatedAt: p.updatedAt || new Date().getTime(),
          createdAt: p.createdAt || new Date().getTime(),
        }
      }),
      taxes:
        d.taxLabel || d.taxRate
          ? [{ name: d.taxLabel || null, rate: +d.taxRate || 0 }]
          : [],
      archived: d.archived || false,
      shippingAddress: d.shippingAddress || initialAddress,
      shippingAmount: +d.shippingCostAmount || null,
      discount:
        !!+d.discountPercentage || +d.discountAmount
          ? {
              type: d.discountType || DiscountType.PERCENTAGE,
              amount: +d.discountAmount || 0,
              percentage: +d.discountPercentage || 0,
            }
          : null,
      currency: {
        code: d.currency.code,
        symbol: d.currency.symbol,
      },
      paymentInstructions: d.paymentInstructions || null,
      shippingTrackNumber: d.shippingTrackNumber || null,
      docHistory: d.docHistory || [],

      taxType: d.taxType,
      integrations: authStore.isStripeConnected
        ? ['Stripe']
        : [],
      taxIncluded: typeof d.taxIncluded === 'boolean' ? d.taxIncluded : true,
    }
  }

  async mapDocumentForGenerator(
    current: any,
    paymentInstructions?: string,
    type?: DOCUMENTTYPE
  ): Promise<Invoice | undefined> {
    const bAdd = current.documentClient?.billingAddress
    return {
      id: current.id,
      base64: {
        _logo: '',
        _signature: '',
        _photos: [],
      },
      logo: current.documentLegalEntity?.logo || '',
      // logo: current.logo ? helpers.getServerUrlByCode(current.logo),
      discountPercentage: current.discount?.percentage || '',
      discountAmount: current.discount?.amount || '',
      discountType: current.discount?.type || '',
      discountLabel: 'Discount',
      createdAt: current.createdAt,
      documentType: !current.documentType
        ? type === DOCUMENTTYPE.EST
          ? 'Estimate'
          : 'Invoice'
        : current.documentType,
      companyId: current.documentLegalEntity?.legalEntityId || '',
      companyName: current.documentLegalEntity?.legalName || '',
      companyPhone: current.documentLegalEntity?.phone || '',
      companyEmail: current.documentLegalEntity?.email || '',
      companyWebsite: current.documentLegalEntity?.website || '',
      companyAddress: current.documentLegalEntity?.address || {},
      companyNotes: current.documentLegalEntity?.noteForClients || '',
      disablePaymentLinks: current.documentLegalEntity?.disablePaymentLinks ?? true,
      billTo: 'Bill to',
      billFrom: 'From',
      subTotal: current.subtotal || '',
      total: current.total || '',
      shipTo: 'Ship to',
      clientId: current.documentClient?.clientId || '',
      clientName: current.documentClient?.name || '',
      clientBusinessName: current.documentClient?.name || '',
      clientMobile: current.documentClient?.mobilePhone || '',
      clientPhone: current.documentClient?.phone || '',
      clientEmail: current.documentClient?.email || '',
      shippingCostAmount: current.shippingAmount || '',
      amountPaid: this.getPaid(current?.payments) || '',
      shippingCostLabel: 'Shipping Cost',
      shippingAddress: current.shippingAddress || null,
      clientShippingAddress: current.documentClient?.shippingAddress || {},
      shippingTrackNumber: current.shippingTrackNumber || '',
      clientBillingAddress: bAdd || {},
      invoiceTitleLabel:
        type === DOCUMENTTYPE.EST ? 'Estimate no.:' : 'Invoice no.:',
      documentNumber: current.number || '',
      signature: current.documentLegalEntity?.signature || '',
      issueDateLabel:
        type === DOCUMENTTYPE.EST ? 'Estimate date:' : 'Invoice date:',
      issueDate: current.issueDate || '',
      dueDateLabel: 'Due:',
      dueDate: current.dueDate || '',
      productLineDescription: 'DESCRIPTION',
      productLineQuantity: 'QTY',
      productLineTax: 'TAX',
      productLineDisc: 'DISC',
      productLineQuantityRate: 'RATE',
      productLineQuantityAmount: 'AMOUNT',
      documentItems: current.documentItems?.length
        ? current.documentItems
            .sort((a: { order: string }, b: { order: string }) =>
              typeof +a.order === 'number' && typeof +b.order === 'number'
                ? +a.order - +b.order
                : 0
            )
            .map(
              (
                el: {
                  taxAmount: string
                  isTaxable: any
                  total: string
                  itemName: any
                  order: any
                  qty: any
                  tax: {
                    name: string
                    rate: any
                  }
                  discount?: {
                    type: string
                    percentage: string
                    amount: string
                  }
                  price: any
                  subtotal: any
                },
                index: number
              ) => {
                return {
                  itemName: el.itemName || '',
                  itemDescription: '',
                  qty: String(el.qty) || '',
                  itemTaxName: el.tax?.name || '',
                  itemTax: el.tax?.rate || '',
                  itemTaxAmount: el.taxAmount || '',
                  itemOrder: el.order || index,
                  itemPrice: el.price || '',
                  itemDiscountType: el.discount?.type,
                  itemDiscountPercentage: el.discount?.percentage,
                  itemDiscountAmount: el.discount?.amount,
                  itemTotal: el.total || '',
                  itemSubTotal: el.subtotal || '',
                  isTaxable: el.isTaxable,
                  // _id: String(Math.random()),
                }
              }
            )
        : [{ ...initialProductLine }],
      subTotalLabel: 'Subtotal',
      taxLabel: Array.isArray(current.taxes) ? current.taxes[0]?.name : '',
      taxRate: Array.isArray(current.taxes) ? current.taxes[0]?.rate : '',
      totalLabel: 'Total',
      balanceLabel: 'Balance Due',
      amountPaidLabel: 'Amounts paid',
      currency: current.currency || helpers.getCurrency(),
      paymentLabel: 'Payment instruction',
      paymentInstructions:
        current.paymentInstructions || paymentInstructions || '',
      notesLabel: 'Notes',
      notes: current.note || '',
      dimensions: {
        width: '',
        height: '',
      },
      photosLabel: 'Photos',
      // photos: current.photos.length ? current.photos.map((el: string | undefined) => helpers.getServerUrlByCode(el)) : [],
      photos: current.photos || [],
      docHistory: current.docHistory || [],
      photosDimensions: {},

      sentState: current.sentState,
      dueType: current.dueType,
      uiFormatter: current.uiFormatter,
      terms: current.terms || '',
      stripeLink: current.integrations?.[0] ? 'https://saldoinvoice.com' : '',
      clientNote: current.documentClient?.note || '',
      payments: current.payments || [],
      companyTaxNumber: current.documentLegalEntity?.taxNumber || '',
      taxIncluded:
        typeof current.taxIncluded === 'boolean' ? current.taxIncluded : true,
      taxType: current.taxType,
      archived: current.archived || false,
      __type: type || undefined,
    }
  }

  resetInvoices() {
    this.invoices = []
    this.estimates = []
    this.documents = []
    this.invoicesFilterModel = {
      currency: helpers.getCurrency().code,
      dateRange: undefined,
      dateFrom: undefined,
      dateTo: undefined,

      sortBy: 'issueDate',
      search: undefined,
      offset: 0,
      limit: 10,
      type: undefined,
      sortType: 'DESC',
    }
    this.estimatesFilterModel = {
      currency: helpers.getCurrency().code,
      dateRange: undefined,
      dateFrom: undefined,
      dateTo: undefined,

      sortBy: 'issueDate',
      search: undefined,
      offset: 0,
      limit: 10,
      type: undefined,
      sortType: 'DESC',
    }
    this.total = 0
  }

  getDueTypeByDate(invoice: Invoice) {
    const dueDate = dayjs(invoice.dueDate).startOf('day')
    const issueDate = dayjs(invoice.issueDate).startOf('day')

    if (issueDate.isSame(dueDate)) {
      return DueType.ON_RECEIPT
    } else if (issueDate.add(1, 'day').isSame(dueDate)) {
      return DueType.NEXT_DAY
    } else if (issueDate.add(7, 'day').isSame(dueDate)) {
      return DueType.DAYS_7
    } else if (issueDate.add(30, 'day').isSame(dueDate)) {
      return DueType.DAYS_30
    }

    return DueType.CUSTOM_DATE
  }

  getDueDateByType(type: any, el: any) {
    const dateObj = new Date(el.issueDate)

    switch (type) {
      case DueType.CUSTOM_DATE:
        return el.dueDate
      case DueType.ON_RECEIPT:
        return el.issueDate
      case DueType.NEXT_DAY:
        return dateObj.setDate(dateObj.getDate() + 1)
      case DueType.DAYS_7:
        return dateObj.setDate(dateObj.getDate() + 7)
      case DueType.DAYS_30:
        return dateObj.setDate(dateObj.getDate() + 30)
      default:
        break
    }
  }

  getIsOverDue(paidState: PaidState, dueDateByType: number) {
    if (paidState === PaidState.STATUS_PAID) return false
    const oneDay = 1000 * 60 * 60 * 24

    const now = new Date()
    const dueDate = new Date(dueDateByType)

    if (now.getFullYear() - new Date(dueDateByType).getFullYear() > 0) {
      return true
    } else if (
      new Date().getFullYear() - new Date(dueDateByType).getFullYear() ===
      0
    ) {
      const start = new Date(now.getFullYear(), 0, 0)
      const diff =
        // @ts-ignore
        now -
        // @ts-ignore
        start +
        (start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000
      const diffDue =
        // @ts-ignore
        dueDate -
        // @ts-ignore
        start +
        (start.getTimezoneOffset() - dueDate.getTimezoneOffset()) * 60 * 1000
      const todayDayOfYear = Math.floor(diff / oneDay)
      const dueDayOfYear = Math.floor(diffDue / oneDay)

      return todayDayOfYear - dueDayOfYear > 0
    } else {
      return false
    }
  }

  getPaidState(total: number, paid: number) {
    if (total > 0 && paid > 0 && total > paid)
      return PaidState.STATUS_PARTIALLY_PAID
    if (total > 0 && paid > 0 && total <= paid) return PaidState.STATUS_PAID
    else return PaidState.STATUS_UNPAID
  }

  setIsPaywall(isOpen: boolean) {
    this.isPayWall = isOpen
    if (!isOpen) {
      this.isPayWallSource = undefined
    }
  }

  setIsPaywallSource(source: string) {
    this.isPayWallSource = source
  }

  async getDocAmount() {
    try {
      const data = await invoicesService.getAmount()

      runInAction(() => {
        this.docAmount = data
      })
    } catch (err) {
      console.error(err)
    }
  }
}

const invoicesStore = new InvoicesStore()
export const invoicesStoreContext = createContext(invoicesStore)
export default invoicesStore
